import classNames from "classnames";
import CiroButton, { CiroButtonStyleEnum } from "../../shared/CiroButton";
import { useContext } from "react";
import CuratedSearchFilterCurrentCompanySection from "./CuratedSearchFilterSection/CuratedSearchFilterCurrentCompanySection";
import AutopilotCuratedSearchContext from "../../../contexts/AutopilotCuratedSearchContext";
import { toast } from "react-hot-toast";
import {
  getScoringCriteriaErrors,
  hasFiltersApplied,
} from "../curatedSearchUtils";
import CuratedSearchCiroInternalIndustrySection from "./CuratedSearchFilterSection/CiroInternalFilters/CuratesSearchCiroInternalIndustrySection";
import CuratedSearchCiroInternalGeographySection from "./CuratedSearchFilterSection/CiroInternalFilters/CuratedSearchCiroInternalGeographySection";
import CuratedSearchCiroInternalCompanyHeadCountSection from "./CuratedSearchFilterSection/CiroInternalFilters/CuratedSearchCiroInternalCompanyHeadCountSection";
import { AdjustmentsVerticalIcon } from "@heroicons/react/24/outline";
import CuratedSearchCiroInternalCompanyKeyWordsSection from "./CuratedSearchFilterSection/CiroInternalFilters/CuratedSearchCiroInternalCompanyKeyWordsSection";
import isValidBooleanExpression from "../../../utils/isValidBooleanExpression";
import CuratedSearchCiroInternalJobTitleSection from "./CuratedSearchFilterSection/CiroInternalFilters/CuratedSearchCiroInternalJobTitleSection";
import CuratedSearchCardHeader from "../CuratedSearchCard/CuratedSearchCardHeader";
import CuratedSearchCiroInternalCompanyRegionSection from "./CuratedSearchFilterSection/CiroInternalFilters/CuratedSearchCiroInternalCompanyRegionSection";
import CuratedSearchCiroInternalCompanyCitySection from "./CuratedSearchFilterSection/CiroInternalFilters/CuratedSearchCiroInternalCompanyCitySection";
import CuratedSearchCiroInternalContactCitySection from "./CuratedSearchFilterSection/CiroInternalFilters/CuratedSearchCiroInternalContactCitySection";
import CuratedSearchCiroInternalContactRegionSection from "./CuratedSearchFilterSection/CiroInternalFilters/CuratedSearchCiroInternalContactRegionSection";
import { InternalCuratedSearchFilters } from "../../../__generated__/graphql";

const INTERNAL_SEARCH_FILTER_MAX_LENGTH = 255;

const filterPastMaxLength = (
  filter: { label: string; id?: string | number }[],
) => {
  const filterString = filter.map((f) => f.id || f.label).join(" OR ");
  const encodedFilterString = encodeURIComponent(filterString);
  return encodedFilterString.length > INTERNAL_SEARCH_FILTER_MAX_LENGTH;
};

interface ICuratedInternalSearchFilterCard {
  onSave: () => Promise<void>;
  curatedSearchId: number | null;
  scoringCriteriaValues: string[];
  hideHeader?: boolean;
  setSavedFilters: (value: InternalCuratedSearchFilters | null) => void;
  savedFilters: InternalCuratedSearchFilters | null;
}

const CuratedInternalSearchFilterCard = ({
  onSave,
  curatedSearchId,
  scoringCriteriaValues,
  hideHeader = false,
  savedFilters,
  setSavedFilters,
}: ICuratedInternalSearchFilterCard) => {
  const { upsertAutopilotCuratedSearchLoading } = useContext(
    AutopilotCuratedSearchContext,
  );

  const hasJobTitleFilterError =
    (savedFilters?.jobTitles?.length || 0) >
      INTERNAL_SEARCH_FILTER_MAX_LENGTH ||
    (Boolean(savedFilters?.jobTitles) &&
      !isValidBooleanExpression(savedFilters?.jobTitles || ""));

  const hasIndustryFilterError = filterPastMaxLength(
    savedFilters?.industries || [],
  );

  const hasGeographyFilterError = filterPastMaxLength(
    savedFilters?.geographies || [],
  );

  const hasContactRegionFilterError = filterPastMaxLength(
    savedFilters?.contactRegions || [],
  );

  const hasContactCityFilterError = filterPastMaxLength(
    savedFilters?.contactCities || [],
  );

  const hasCompanyHqFilterError = filterPastMaxLength(
    savedFilters?.companyHQLocations || [],
  );

  const hasCompanyHqRegionFilterError = filterPastMaxLength(
    savedFilters?.companyHQRegions || [],
  );

  const hasCompanyHqCityFilterError = filterPastMaxLength(
    savedFilters?.companyHQCities || [],
  );

  const hasCompanyKeyWordsFilterError =
    (savedFilters?.companyKeywords?.length || 0) >
      INTERNAL_SEARCH_FILTER_MAX_LENGTH ||
    (Boolean(savedFilters?.companyKeywords) &&
      !isValidBooleanExpression(savedFilters?.companyKeywords || ""));

  const hasProfileSummaryKeywordsFilterError =
    (savedFilters?.profileSummaryKeywords?.length || 0) >
      INTERNAL_SEARCH_FILTER_MAX_LENGTH ||
    (Boolean(savedFilters?.profileSummaryKeywords) &&
      !isValidBooleanExpression(savedFilters?.profileSummaryKeywords || ""));

  const scoringCriteriaErrors = getScoringCriteriaErrors(scoringCriteriaValues);

  const hasScoringCriteriaErrors = scoringCriteriaErrors.some(
    (error) => error !== null,
  );

  return (
    <div>
      {!hideHeader && (
        <CuratedSearchCardHeader
          headerText="Filters"
          headerIcon={
            <AdjustmentsVerticalIcon className="h-5 w-5 text-orange-500" />
          }
        />
      )}
      <div
        className={classNames("grid", "grid-cols-1", "xl:grid-cols-2", "gap-2")}
      >
        <CuratedSearchCiroInternalIndustrySection
          defaultOpen={false}
          hasError={hasIndustryFilterError}
          currentIndustryFilter={
            (savedFilters?.industries ?? []) as {
              label: string;
              id: number;
            }[]
          }
          setCurrentIndustryFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              industries: v,
            });
          }}
        />
        <CuratedSearchCiroInternalJobTitleSection
          defaultOpen={false}
          hasError={hasJobTitleFilterError}
          jobTitleFilter={savedFilters?.jobTitles || null}
          setJobTitleFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              jobTitles: v,
            });
          }}
        />
        <CuratedSearchCiroInternalCompanyKeyWordsSection
          hasError={hasCompanyKeyWordsFilterError}
          currentKeyWordsFilter={savedFilters?.companyKeywords || null}
          setCurrentKeyWordsFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              companyKeywords: v,
            });
          }}
        />
        {/* TODO: Add back once we figure out latency with Proxycurl CIR-3434 */}
        {/* <CuratedSearchCiroInternalProfileSummaryKeywordsSection
          hasError={hasProfileSummaryKeywordsFilterError}
          currentProfileSummaryKeywords={currentFilters?.profileSummaryKeywords ?? null}
          setCurrentProfileSummaryKeywords={(v) => {
            setCurrentFilters({
              ...currentFilters,
              profileSummaryKeywords: v,
            });
          }}
        /> */}
        <CuratedSearchCiroInternalContactCitySection
          defaultOpen={false}
          hasError={hasContactCityFilterError}
          currentContactCityFilter={
            (savedFilters?.contactCities ?? []) as {
              label: string;
            }[]
          }
          setCurrentContactCityFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              contactCities: v,
            });
          }}
        />
        <CuratedSearchCiroInternalContactRegionSection
          defaultOpen={false}
          hasError={hasContactRegionFilterError}
          currentContactRegionFilter={
            (savedFilters?.contactRegions ?? []) as {
              label: string;
            }[]
          }
          setCurrentContactRegionFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              contactRegions: v,
            });
          }}
        />
        <CuratedSearchCiroInternalGeographySection
          defaultOpen={false}
          hasError={hasGeographyFilterError}
          currentGeographyFilter={
            (savedFilters?.geographies ?? []) as {
              label: string;
              id: string;
            }[]
          }
          setCurrentGeographyFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              geographies: v,
            });
          }}
        />
        <CuratedSearchCiroInternalCompanyCitySection
          defaultOpen={false}
          currentCompanyCityFilter={
            (savedFilters?.companyHQCities ?? []) as {
              label: string;
            }[]
          }
          hasError={hasCompanyHqCityFilterError}
          setCurrentCompanyCityFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              companyHQCities: v,
            });
          }}
        />
        <CuratedSearchCiroInternalCompanyRegionSection
          defaultOpen={false}
          currentCompanyRegionFilter={
            (savedFilters?.companyHQRegions ?? []) as {
              label: string;
            }[]
          }
          hasError={hasCompanyHqRegionFilterError}
          setCurrentCompanyRegionFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              companyHQRegions: v,
            });
          }}
        />
        {/* CIR-4043 hiding companyHQLocations for now until we add the field to the index */}
        {/* <CuratedSearchCiroInternalCompanyHqSection
          defaultOpen={false}
          currentCompanyHqFilter={
            (savedFilters?.companyHQLocations ?? []) as {
              label: string;
              id: string;
            }[]
          }
          hasError={hasCompanyHqFilterError}
          setCurrentCompanyHqFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              companyHQLocations: v,
            });
          }}
        /> */}
        <CuratedSearchFilterCurrentCompanySection
          defaultOpen={false}
          currentCompanyFilter={
            (savedFilters?.companyNames ?? []) as {
              label: string;
            }[]
          }
          setCurrentCompanyFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              companyNames: v,
            });
          }}
        />
        <CuratedSearchCiroInternalCompanyHeadCountSection
          currentCompanyHeadCountFilter={
            savedFilters?.companyHeadCountRange ?? null
          }
          setCurrentCompanyHeadCountFilter={(v) => {
            setSavedFilters({
              ...savedFilters,
              companyHeadCountRange: v,
            });
          }}
        />
      </div>
      <div className={classNames("flex", "justify-end", "gap-2", "pt-2")}>
        <CiroButton
          analyticsField="Save Internal Ciro Database Filters"
          style={CiroButtonStyleEnum.LOUD}
          disabled={
            !hasFiltersApplied({ filters: savedFilters, type: "internal" }) ||
            hasJobTitleFilterError ||
            hasIndustryFilterError ||
            hasGeographyFilterError ||
            hasContactCityFilterError ||
            hasContactRegionFilterError ||
            hasCompanyHqFilterError ||
            hasCompanyHqRegionFilterError ||
            hasCompanyHqCityFilterError ||
            hasCompanyKeyWordsFilterError ||
            hasProfileSummaryKeywordsFilterError ||
            upsertAutopilotCuratedSearchLoading
            // Note: hasScoringCriteriaErrors is removed to allow clicking with scoring criteria errors
          }
          onClick={async () => {
            if (!savedFilters) {
              toast.error("No filters applied");
              return;
            }
            if (hasScoringCriteriaErrors) {
              toast.error("Please fix errors in scoring criteria");
              return;
            }
            await onSave();
          }}
        >
          {Boolean(curatedSearchId) ? "Regenerate preview" : "Preview results"}
        </CiroButton>
      </div>
    </div>
  );
};

export default CuratedInternalSearchFilterCard;
